import React from 'react'
import clsx from 'clsx'
import Drawer from '@material-ui/core/Drawer'
import List from '@material-ui/core/List'
import { makeStyles } from '@material-ui/core/styles'
import SidebarItem from './sidebar-item'
import { drawerWidth } from './styles'
import { useIsDesktopNav, useIsCollapsibleSidebar } from 'lib/hooks'

// Drawer slot styling lives here as a makeStyles ruleset (rather than a
// styled() definition). MUI v4 in development mode generates hashed
// class names (e.g. `MuiDrawer-paper-1811`), so descendant selectors
// like `& .MuiDrawer-paper` do not match. The canonical MUI v4 way to
// style slots is via the component's `classes` prop, which is what we
// do here. When MUI v5/v6 lands, this can migrate to slot-aware
// `styled()` definitions in one place.
const useDrawerStyles = makeStyles(theme => ({
  drawerContainer: ({ sidebarLayoutWidth }) => ({
    flexShrink: 0,
    ...(sidebarLayoutWidth > 0 && {
      width: sidebarLayoutWidth,
      whiteSpace: 'nowrap',
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    }),
  }),
  drawerPaper: ({ assumedUser }) => ({
    width: drawerWidth,
    borderRight: '1px solid #E0E0E0',
    marginTop: assumedUser ? '30px' : 'unset',
    [theme.breakpoints.only('xs')]: {
      marginTop: assumedUser ? '70px' : 'unset',
    },
  }),
  drawerOpen: ({ assumedUser, sidebarLayoutWidth }) => ({
    width: sidebarLayoutWidth,
    maxWidth: sidebarLayoutWidth,
    boxSizing: 'border-box',
    overflowX: 'visible',
    whiteSpace: 'normal',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginTop: assumedUser ? '30px' : 'unset',
    [theme.breakpoints.only('xs')]: {
      marginTop: assumedUser ? '70px' : 'unset',
    },
    '& .MuiListItemIcon-root': {
      minWidth: 24 + theme.spacing(1),
      marginRight: theme.spacing(1),
      justifyContent: 'flex-start',
    },
  }),
  drawerClose: ({ assumedUser, sidebarLayoutWidth }) => ({
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: sidebarLayoutWidth,
    maxWidth: sidebarLayoutWidth,
    boxSizing: 'border-box',
    marginTop: assumedUser ? '30px' : 'unset',
    [theme.breakpoints.only('xs')]: {
      marginTop: assumedUser ? '70px' : 'unset',
    },
    '& .MuiListItem-root': {
      justifyContent: 'center',
      paddingLeft: `${theme.spacing(1.5)}px !important`,
      paddingRight: `${theme.spacing(1.5)}px !important`,
    },
    '& .MuiListItemIcon-root': {
      minWidth: '0 !important',
      marginRight: '0 !important',
      justifyContent: 'center',
    },
    '& .MuiListItemSecondaryAction-root': {
      display: 'none !important',
    },
  }),
  menu: {
    padding: 0,
    paddingTop: theme.spacing(1.5),
  },
  toolbarSpacing: {
    ...theme.mixins.toolbar,
    minHeight: '61px',
  },
}))

export default function Sidebar({
  menuItems,
  isMobileNavOpen,
  isTabletNavOpen,
  setIsTabletNavOpen,
  handleDrawerToggle,
  assumedUser,
  drawerContainer,
  location,
  sidebarLayoutWidth = 0,
  showNavLabels = true,
  isNavCollapsed = false,
}) {
  const classes = useDrawerStyles({ assumedUser, sidebarLayoutWidth })
  const isDesktopNav = useIsDesktopNav()
  const isCollapsibleSidebar = useIsCollapsibleSidebar()
  const isMobileNav = !isDesktopNav && !isCollapsibleSidebar
  const collapsed = !showNavLabels

  if (!menuItems.length) return null

  const renderMenu = () => (
    <List className={classes.menu}>
      {menuItems.map(item => {
        const ItemComponent = item?.component ? item.component : SidebarItem
        return (
          <ItemComponent
            {...{
              onClick: isMobileNav ? handleDrawerToggle : undefined,
              key: item.path || item.label,
              ...item,
              location,
              isCollapsibleSidebar,
              isNavCollapsed,
              showNavLabels,
              isTabletNavOpen,
              setIsTabletNavOpen,
              collapsed,
            }}
          />
        )
      })}
    </List>
  )

  if (isDesktopNav) {
    return (
      <nav className={classes.drawerContainer}>
        <Drawer
          color="primary"
          variant="persistent"
          open
          classes={{
            paper: clsx(classes.drawerPaper, classes.drawerOpen),
          }}
        >
          <div className={classes.toolbarSpacing} />
          {renderMenu()}
        </Drawer>
      </nav>
    )
  }

  if (isMobileNav) {
    return (
      <nav>
        <Drawer
          container={drawerContainer}
          variant="temporary"
          anchor="left"
          open={isMobileNavOpen}
          onClose={handleDrawerToggle}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {renderMenu()}
        </Drawer>
      </nav>
    )
  }

  return (
    <nav className={classes.drawerContainer}>
      <Drawer
        variant="permanent"
        open={isTabletNavOpen}
        classes={{
          paper: clsx(classes.drawerPaper, {
            [classes.drawerOpen]: !isNavCollapsed,
            [classes.drawerClose]: isNavCollapsed,
          }),
        }}
      >
        <div className={classes.toolbarSpacing} />
        {renderMenu()}
      </Drawer>
    </nav>
  )
}
